import { ENDPOINTS } from '../config.js';

/**
 * 物件データの検証。外部から来るJSONは一切信用せず、
 * 必須項目の欠落は即エラーにする（fail fast）。
 */
export function validateProperty(raw) {
  if (!raw || typeof raw !== 'object') {
    throw new Error('物件データが取得できませんでした');
  }
  const { id, name, splat, viewpoints } = raw;
  if (typeof id !== 'string' || id.length === 0) {
    throw new Error('物件データに id がありません');
  }
  if (typeof name !== 'string' || name.length === 0) {
    throw new Error('物件データに name がありません');
  }

  /*
   * 物件には3つの形がある。
   * - スプラット（splat + viewpoints）… 写真のようにリアルな3D内見
   * - メッシュ  （model + viewpoints）… 面があり当たり判定が正確な3D内見
   * - 写真      （photos）            … 写真を切り替えて見る内見
   * 3Dが無い間は写真で公開する。
   * 両方ある場合はメッシュを使う（スプラットは撮影範囲外が崩れるため）。
   */
  const hasSplat = typeof splat === 'string' && splat.length > 0;
  const model = normalizeModelPath(raw.model);
  const plan = typeof raw.plan === 'string' && /^[a-z0-9_-]{1,64}$/i.test(raw.plan) ? raw.plan : null;
  const has3d = hasSplat || model !== null || plan !== null;
  const photos = normalizePhotos(raw.photos);

  if (!has3d && photos.length === 0) {
    throw new Error('物件データに 3Dスキャン（splat / model / plan）も photos（写真）もありません');
  }
  if (has3d && (!Array.isArray(viewpoints) || viewpoints.length === 0)) {
    throw new Error('3Dスキャンの物件には viewpoints（部屋ごとの視点）が必要です');
  }

  const normalized = has3d ? viewpoints.map((vp, i) => normalizeViewpoint(vp, i)) : [];

  return Object.freeze({
    id,
    name,
    /** スプラットが無ければ null */
    splat: hasSplat ? splat : null,
    /** メッシュ（.usdz / .glb）が無ければ null */
    model,
    /** 設計データから組み立てる CG の物件（src/data/plans/）。無ければ null */
    plan,
    address: typeof raw.address === 'string' ? raw.address : '',
    /** スキャン時のワールド姿勢補正（Scaniverse等は上下が反転して出ることがある） */
    transform: normalizeTransform(raw.transform),
    facts: normalizeFacts(raw.facts),
    /** 間取りミニマップ。無い物件もあるので null を許容する */
    floorplan: normalizeFloorplan(raw.floorplan),
    viewpoints: Object.freeze(normalized),
    photos: Object.freeze(photos),
    /** 自動判定では通れない所を人が「通れる」と指定する通路（メッシュの歩行用） */
    walkLinks: Object.freeze(normalizeWalkLinks(raw.walkLinks)),
  });
}

/** [{ from: [x, z] または [x, z, 高さ], to: 同じ形 }] だけを受け付ける */
function normalizeWalkLinks(links) {
  if (!Array.isArray(links)) return [];
  const isXZ = (v) =>
    Array.isArray(v) && (v.length === 2 || v.length === 3) && v.every((n) => typeof n === 'number' && Number.isFinite(n));
  return links
    .filter((l) => l && isXZ(l.from) && isXZ(l.to))
    .map((l) => Object.freeze({ from: Object.freeze([...l.from]), to: Object.freeze([...l.to]) }));
}

/**
 * メッシュのパス。public/model/ 配下の相対パスで、対応する形式だけを許可する。
 * 親ディレクトリへ上がる指定は弾く。
 */
function normalizeModelPath(model) {
  if (typeof model !== 'string' || model.length === 0) return null;
  if (model.includes('..') || model.startsWith('/') || model.includes('\\')) return null;
  return /\.(usdz|glb|gltf|fbx)$/i.test(model) ? model : null;
}

/**
 * 写真の一覧。room ごとにまとめて表示する。
 * src は自サイト内の相対パスだけを許可し、外部URLは弾く。
 */
function normalizePhotos(photos) {
  if (!Array.isArray(photos)) return [];
  return photos
    .filter(
      (p) =>
        p &&
        typeof p.room === 'string' &&
        p.room.length > 0 &&
        typeof p.src === 'string' &&
        p.src.startsWith('/') &&
        !p.src.startsWith('//'),
    )
    .map((p) =>
      Object.freeze({
        room: p.room,
        src: p.src,
        caption: typeof p.caption === 'string' ? p.caption : '',
      }),
    );
}

/** 写真から部屋の一覧を作る。JSONに書かれた順番を保つ */
export function roomsOf(property) {
  const seen = [];
  for (const photo of property.photos) {
    if (!seen.includes(photo.room)) seen.push(photo.room);
  }
  return seen;
}

/**
 * 間取り図の設定。座標は画像左上を(0,0)、右下を(1,1)とした相対値で持つ。
 * 画像の表示サイズが変わっても位置がずれないようにするため。
 */
function normalizeFloorplan(fp) {
  if (!fp || typeof fp !== 'object') return null;
  if (typeof fp.image !== 'string' || fp.image.length === 0) return null;

  const marks = Array.isArray(fp.marks)
    ? fp.marks
        .filter(
          (m) =>
            m &&
            typeof m.name === 'string' &&
            m.name.length > 0 &&
            isRatio(m.x) &&
            isRatio(m.y),
        )
        .map((m) => Object.freeze({ name: m.name, x: m.x, y: m.y }))
    : [];

  // 印がまだ1つも無くても図は出す。視点作成モードで印を置けなくなるため。
  return Object.freeze({ image: fp.image, marks: Object.freeze(marks) });
}

function isRatio(n) {
  return typeof n === 'number' && Number.isFinite(n) && n >= 0 && n <= 1;
}

function normalizeViewpoint(vp, index) {
  if (!vp || typeof vp.name !== 'string') {
    throw new Error(`viewpoints[${index}] に name がありません`);
  }
  const position = toVec3(vp.position);
  const lookAt = toVec3(vp.lookAt);
  if (!position || !lookAt) {
    throw new Error(`viewpoints[${index}]（${vp.name}）の position / lookAt が不正です`);
  }
  return Object.freeze({ name: vp.name, position, lookAt });
}

function normalizeTransform(t) {
  const base = { rotationX: 0, rotationY: 0, rotationZ: 0, scale: 1, position: [0, 0, 0] };
  if (!t || typeof t !== 'object') return Object.freeze(base);
  const position = toVec3(t.position) ?? base.position;
  return Object.freeze({
    rotationX: numberOr(t.rotationX, 0),
    rotationY: numberOr(t.rotationY, 0),
    rotationZ: numberOr(t.rotationZ, 0),
    scale: numberOr(t.scale, 1),
    position,
  });
}

/** AI内見が参照する物件事実。値は文字列に正規化して表示・プロンプト双方で使う */
function normalizeFacts(facts) {
  if (!facts || typeof facts !== 'object' || Array.isArray(facts)) return Object.freeze({});
  const entries = Object.entries(facts)
    .filter(([k, v]) => typeof k === 'string' && v != null)
    .map(([k, v]) => [k, String(v)]);
  return Object.freeze(Object.fromEntries(entries));
}

function toVec3(v) {
  if (!Array.isArray(v) || v.length !== 3) return null;
  if (!v.every((n) => typeof n === 'number' && Number.isFinite(n))) return null;
  return Object.freeze([...v]);
}

function numberOr(v, fallback) {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

export async function fetchProperty(id) {
  const res = await fetch(ENDPOINTS.property(id), { headers: { accept: 'application/json' } });
  if (!res.ok) {
    throw new Error(
      res.status === 404
        ? `物件「${id}」が見つかりません`
        : `物件データの取得に失敗しました（${res.status}）`,
    );
  }
  return validateProperty(await res.json());
}
